<template>
	<div>
		<div class="d-flex justify-content-between ts-mb-3">
			<div>
				<h1 class="page-header ts-mb-0">
					{{ $t('exchangeRate.pageTitle') }}
				</h1>
			</div>
			<ts-breadcrumb :items="[
				{ text: $t('toHomePage'), href: '/' },
				{
					text: $t('exchangeRate.pageTitle'),
					active: true
				}
			]" />
		</div>
		<ts-card>
			<ts-card-wrapper>
				<div class="ts-flex ts-justify-between ts-items-center">
					<div>
						<ts-button color="info" outline @click="addNew">
							{{ $t('add_new') }}
						</ts-button>
					</div>
					<div>
						<a-input-search v-model="search" @search="fetchData()"
							:placeholder="$t('exchangeRate.search')"></a-input-search>
					</div>
				</div>
			</ts-card-wrapper>
			<div class="overflow-auto ts-mt-4">
				<Table :columns="columns" :data="resources" :loading="loading" max-height="700" stripe highlight-row
					size="small">
					<template slot-scope="{ row }" slot="is_default">
						<div v-if="row.is_default" class="ts-w-12 ts-px-3 ts-py-1 ts-rounded-md ts-bg-green-200 ts-text-green-500">
							Yes</div>
						<div v-else class="ts-w-12 ts-px-3 ts-py-1 ts-rounded-md ts-bg-red-200 ts-text-red-500">No</div>
					</template>
					<template slot-scope="{ row }" slot="action">
						<div class="ts-flex ts-space-x-2 ts-items-center">
							<a-popconfirm placement="topLeft" :ok-text="$t('yes')" :cancel-text="$t('no')"
								@confirm="clickDeleteHandler(row)">
								<template slot="title">
									<p>
										{{ $t('are_you_sure_to_delete') }}
									</p>
								</template>
								<a href="#" v-tooltip="$t('delete')"
									class="ts-text-red-500 ts-h-6 ts-w-6 ts-flex ts-items-center ts-justify-center ts-bg-red-100 ts-rounded-full hover:ts-no-underline hover:ts-text-red-500"
									:disabled="row._deleting">
									<i class="fas fa-circle-notch spin" v-if="row._deleting"></i>
									<i class="fas fa-trash" v-else></i>
								</a>
							</a-popconfirm>
						</div>
					</template>
				</Table>
			</div>
			<div class="d-flex justify-content-between align-items-center p-15">
				<ts-total-list v-model="pagination" />
				<ts-pagination v-model="pagination" @navigate="fetchData"></ts-pagination>
			</div>
		</ts-card>
		<a-modal :visible="showForm" :footer="null" centered :title="$t('currency.pageTitle')" @cancel="onCancel">
			<ts-loading-banner :loading="saving">
				<div class="ts-space-y-2">
					<div class="row">
						<div class="col-md-12">
							<label class="required">{{ $t('exchangeRate.fromCurrency') }}</label>
							<a-select v-model="model.target_currency_id" show-search :filter-option="filterOption" style="width: 100%"
								:options="currencyOpt"></a-select>
							<div v-if="errors.has('target_currency_id')">
								<span class="text-danger">{{ errors.first('target_currency_id') }}</span>
							</div>
						</div>
					</div>
					<div class="row">
						<div class="col-md-12">
							<label class="required">{{ $t('exchangeRate.defaultCurrency') }}</label>
							<a-input disabled :value="defaultCurrency ? defaultCurrency.currency_code : ''"></a-input>
							<div v-if="errors.has('currency_id')">
								<span class="text-danger">{{ errors.first('currency_id') }}</span>
							</div>
						</div>
					</div>
					<div class="row">
						<div class="col-md-12">
							<label class="required">{{ $t('exchangeRate.exchangeRate') }}</label>
							<a-input type="number" v-model.number="model.exchange_rate"
								@change="exchangeRateInput"
							></a-input>
							<div v-if="errors.has('exchange_rate')">
								<span class="text-danger">{{ errors.first('exchange_rate') }}</span>
							</div>
						</div>
					</div>
					<div class="row">
						<div class="col-md-12 ts-flex ts-justify-end ts-space-x-2">
							<ts-button @click="onCancel">{{ $t('cancel') }}</ts-button>
							<ts-button outline color="primary" @click="clickSaveAddNewHandler">{{ $t('save_add_new')
							}}</ts-button>
							<ts-button color="primary" @click="clickSaveHandler">{{ $t('save') }}</ts-button>
						</div>
					</div>
				</div>
			</ts-loading-banner>
		</a-modal>

	</div>
</template>

<script>
import notificationAlert from '@/utils/NotificationAlert';
import { Errors } from 'form-backend-validation';
import { mapActions, mapState } from 'vuex';

export default {
	name: "exchangeRate",
	data() {
		return {
			loading: false,
			showForm: false,
			saving: false,
			errors: new Errors(),
			currencies: [],
			model: {
				id: undefined,
				currency_id: undefined,
				rate_to_default: undefined,
				target_currency_id: undefined,
				exchange_rate: undefined,
			}
		}
	},
	computed: {
		...mapState('exchangeRate', [
			'resources',
			'pagination',
		]),
		search: {
			get() {
				return this.$store.state.exchangeRate.search
			},
			set(newValue) {
				this.$store.commit('exchangeRate/SET_SEARCH', newValue)
				this.$store.commit('exchangeRate/RESET_CURRENT_PAGE')
			}
		},
		defaultCurrency() {
			return this.currencies.find(c => c.is_default)
		},
		currencyOpt() {
			return this.currencies.filter(c => !c.is_default).map(el => ({
				value: el.currency_id,
				label: el.currency_code
			}))
		},
		columns() {
			return [
				{
					title: this.$t('exchangeRate.fromCurrency'),
					key: 'target_currency'
				},
				{
					title: this.$t('exchangeRate.defaultCurrency'),
					key: 'base_currency'
				},
				{
					title: this.$t('exchangeRate.exchangeRate'),
					key: 'exchange_rate'
				},
				{
					title: this.$t('exchangeRate.createdAt'),
					key: 'created_date'
				},
				{
					title: this.$t('actions'),
					slot: 'action',
					fixed: 'right',
					align: 'center',
					width: 100
				}
			]
		}
	},
	mounted() {
		this.fetchData()
	},
	methods: {
		...mapActions('exchangeRate', ['fetch', 'onDelete', 'onSave', 'onGetCurrency']),
		async addNew() {
			this.clearInput();
			this.showForm = true
			await this.fetchCurrency()
			const findDefault = this.currencies.find(c => c.is_default)
			if (findDefault) {
				this.model.currency_id = findDefault.currency_id
			}
		},
		async fetchCurrency() {
			if (this.currencies.length) return;
			this.saving = true
			await this.onGetCurrency()
				.then((response) => {
					this.currencies = response.data.data
				})
				.catch((errror) => {
					notificationAlert(errror)
					this.errors = new Errors(errror.response.data.errors)
				})
				.finally(() => this.saving = false)
		},
		async fetchData(attributes = {}) {
			this.loading = true
			return await this.fetch(attributes)
				.catch(error => {
					notificationAlert(error)
				})
				.finally(() => {
					this.loading = false
				})
		},
		clickSaveAddNewHandler() {
			this.saving = true
			this.onSave(this.model)
				.then((response) => {
					notificationAlert(response, 'success')
					this.clearInput()
					this.fetchData()
				})
				.catch((errror) => {
					notificationAlert(errror)
					this.errors = new Errors(errror.response.data.errors)
				})
				.finally(() => this.saving = false)
		},
		clickSaveHandler() {
			this.saving = true
			this.onSave(this.model)
				.then((response) => {
					notificationAlert(response, 'success')
					this.fetchData()
					this.clearInput()
					this.showForm = false
				})
				.catch((errror) => {
					notificationAlert(errror)
					this.errors = new Errors(errror.response.data.errors)
				})
				.finally(() => this.saving = false)
		},
		clickDeleteHandler(record) {
			this.saving = true
			this.onDelete(record.id)
				.then((response) => {
					notificationAlert(response, 'success')
					this.fetchData()
				})
				.catch((errror) => {
					notificationAlert(errror)
					this.errors = new Errors(errror.response.data.errors)
				})
				.finally(() => this.saving = false)
		},
		clearInput() {
			this.model.id = undefined
			this.model.currency_id = undefined
			this.model.rate_to_default = undefined
			this.model.target_currency_id = undefined
			this.model.exchange_rate = undefined
		},
		onCancel() {
			this.showForm = false
			this.clearInput()
			this.fetchData()
		},
		filterOption(input, option) {
			return (
				option.componentOptions.children[0].text
					.toLowerCase()
					.indexOf(input.toLowerCase()) >= 0
			)
		},
		exchangeRateInput() {
			this.model.rate_to_default = 1 / this.model.exchange_rate
		}
	}
}
</script>